import { useContext, useEffect, useMemo, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { SocketTopics } from "@shared/types";
import { SocketArgs, SocketResultWrapper } from "@shared/services/socket/types";
import { usePrevious } from "@shared/utils/hooks/misc";
import { SocketContext } from "@shared/features/socket/Socket.context";
import {
  checkInitialDataArgsChanged,
  checkPnlArgsChanged,
  checkPricesArgsChanged,
} from "@shared/features/socket/utils";
import { socketsSlices } from "@shared/slices/sockets.slice";
import { useSelector } from "react-redux";

const useSocket = <Topic extends SocketTopics>(
  topic: Topic,
  args: SocketArgs<Topic> | typeof skipToken,
  onUnsubscribe?: (args?: SocketArgs<Topic> | typeof skipToken) => void,
  delaySubscribe?: boolean
): SocketResultWrapper<Topic> | undefined => {
  const { socketHandler, enabled, useIsFocused } = useContext(SocketContext)!;
  const isFocused = useIsFocused();
  const subscribedArgsRef = useRef<SocketArgs<Topic> | typeof emptyArgs>(
    emptyArgs
  );
  const pendingSubscriptionArgsRef = useRef<
    SocketArgs<Topic> | typeof emptyArgs
  >(emptyArgs);
  const previousArgs = usePrevious(args);
  const dataSelector = useMemo(
    () => socketsSlices[topic].selectors.getArgsDataSelector(args),
    [args]
  );
  const value: SocketResultWrapper<Topic> = useSelector(dataSelector);

  const argsChanged = useMemo(() => {
    if (args === skipToken) return false;
    else if (previousArgs === skipToken) return true;
    else if (topic in TOPIC_TO_CHECK_ARGS_CHANGED) {
      return !!TOPIC_TO_CHECK_ARGS_CHANGED[topic]?.(previousArgs, args);
    }
    return previousArgs !== args;
  }, [previousArgs, args]);

  const handleUnsubscribe = () => {
    if (subscribedArgsRef.current !== emptyArgs) {
      socketHandler.unSubscribe(topic, subscribedArgsRef.current);
      onUnsubscribe?.(subscribedArgsRef.current);
      subscribedArgsRef.current = emptyArgs;
    }
  };

  const unsubscribe = (withoutDelay?: boolean) => {
    if (withoutDelay) {
      handleUnsubscribe();
    } else {
      return setTimeout(() => {
        handleUnsubscribe();
      }, 500);
    }
  };

  const handleSubscribe = () => {
    if (args !== skipToken) {
      if (subscribedArgsRef.current !== emptyArgs) {
        unsubscribe(true);
      }
      socketHandler.subscribe(topic, args);
      subscribedArgsRef.current = args;
      pendingSubscriptionArgsRef.current = emptyArgs;
    }
  };

  const subscribe = () => {
    if (args !== skipToken) {
      pendingSubscriptionArgsRef.current = args;
    }
    if (delaySubscribe) {
      return setTimeout(() => {
        handleSubscribe();
      }, 500);
    } else {
      handleSubscribe();
    }
  };

  useEffect(() => {
    if (!enabled) return;

    let timeout: NodeJS.Timeout | undefined;
    const pendingSubscriptionArgs = pendingSubscriptionArgsRef.current;
    const subscribedArgs = subscribedArgsRef.current;
    const isPendingSubscription = pendingSubscriptionArgs !== emptyArgs;
    const alreadySubscribed = subscribedArgs !== emptyArgs;

    if (
      isFocused &&
      args !== skipToken &&
      ((!alreadySubscribed && !isPendingSubscription) || argsChanged)
    ) {
      timeout = subscribe();
    } else if ((!isFocused || args === skipToken) && alreadySubscribed) {
      timeout = unsubscribe();
    }

    return () => {
      const shouldClearTimeout =
        timeout &&
        isPendingSubscription &&
        pendingSubscriptionArgs !== subscribedArgs;
      if (shouldClearTimeout) {
        clearTimeout(timeout);
      }
    };
  }, [argsChanged, isFocused, args]);

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, []);

  return value;
};

export default useSocket;

export const emptyArgs = Symbol.for("empty_args");

const TOPIC_TO_CHECK_ARGS_CHANGED: {
  [key in SocketTopics]?: any;
} = {
  [SocketTopics.PRICES]: checkPricesArgsChanged,
  [SocketTopics.GRAPHS_DATA]: checkPricesArgsChanged,
  [SocketTopics.AGG_TRADES]: checkInitialDataArgsChanged,
  [SocketTopics.AGG_BOTS]: checkInitialDataArgsChanged,
  [SocketTopics.PNL]: checkPnlArgsChanged,
};
